import React, { ReactElement, use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentOutlined, LinkOutlined } from '@mui/icons-material';
import Constants from 'style/constants';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { isG2Device } from 'shared/models/device-type';
import { DeviceDetailsProvider } from 'shared/providers/device-details/providers/device-details-provider';
import DevicesProviderContext from 'shared/providers/devices/contexts/devices-provider-context';
import { deviceDisplayType } from 'shared/utils/device-utils';

import DeviceDetailsPopup from 'features/device/device-details/views/device-details-popup.component';
import { useDevices } from 'features/device/devices/hooks/use-devices';
import styles from 'features/device/devices/views/devices.module.scss';
import Device, { isRoomDevice } from 'features/device/domain/models/device';
import { DeviceStatus } from 'features/device/domain/models/device-status';
import ReadDevicesCommand from 'features/device/domain/models/read-devices-command';
import DeviceFilter from 'features/device/models/device-filter';
import { FilterValueType } from 'features/device/models/device-filter-value';
import DeviceFilterValues from 'features/device/models/device-filter-values';
import { useSendTestEventContextProvider } from 'features/device/send-test-event/context/send-test-event-provider';
import SendTestEventPopup from 'features/device/send-test-event/views/send-test-event-popup.component';
import DeviceDeletePupup from 'features/device/views/device-delete-popups';
import DeviceFilters from 'features/device/views/device-filters';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import IntegrationsNavigation from 'features/integrations/views/integrations-navigation';

const Devices = (): React.JSX.Element => {
  const { hasPermission } = usePermissionAuthorization();
  const { t } = useTranslation('devices');
  const { t: deviceTypesTranslations } = useTranslation('deviceTypes');
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const {
    readDevicesData,
    readDevicesIsSuccess,
    readDevicesIsLoading,
    readDevicesError,
    triggerReadDevicesQuery,
    triggerReadFilterValuesQuery,
    readFilterValuesData,
    readFilterValuesError,
    readFilterValuesIsLoading,
    readFilterValuesIsSuccess,
    readDevicesQuery,
    updateReadDevicesQuery,
  } = use(DevicesProviderContext);

  const [detailsViewingMode, setDetailsViewingMode] = useState<ViewingMode>('none');
  const [currentDeviceId, setCurrentDeviceId] = useState<string>();
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState<boolean>(false);

  const {
    currentPagination: [currentPagination, setCurrentPagination],
    currentPage,
    currentRowsPerPage,
    setCurrentPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSearchChanged,
  } = useTableFiltering();

  const {
    openDeviceDetailsPopup,
    isDeviceDetailsPopupOpen,
    deviceFiltersFromSessionStorage,
    setDeviceFiltersFromSessionStorage,
    sortFromSessionStorage,
    setSortFromSessionStorage,
    searchBarFromSessionStorage,
    setSearchBarFromSessionStorage,
  } = useDevices();

  const { openSendTestEventPopup, isSendTestEventPopupOpen } = useSendTestEventContextProvider();

  const columns: ColumnDefinition<Device, NestedKeyof<Device>>[] = [
    { key: 'name', label: t('devicesTable.column.name') },
    {
      key: 'type',
      label: t('devicesTable.column.type'),
      renderCustomContentProvider: (device) => <>{deviceDisplayType(deviceTypesTranslations, device)}</>,
      disableSort: true,
    },
    {
      key: 'gateway',
      label: t('devicesTable.column.gateway'),
      renderCustomContentProvider: (device): ReactElement => {
        if (device.gateway) {
          return <>{device.gateway.name}</>;
        } else if (device.system) {
          return <>{device.system.name}</>;
        } else {
          return <></>;
        }
      },
    },
    {
      key: 'source',
      label: t('devicesTable.column.source'),
      disableSort: true,
      tableCellProps: {
        align: 'left',
        width: '200px',
      },
    },
    {
      key: 'isOutdated',
      label: '',
      renderCustomContentProvider: (device) => <div className={styles.iconRow}>{getIcons(device)}</div>,
      disableSort: true,
      tableCellProps: {
        align: 'left',
        width: '40px',
      },
    },
  ];

  const getIcons = (device: Device): ReactElement[] => {
    const result: ReactElement[] = [];
    if (!readDevicesData) {
      return result;
    }

    if (readDevicesData.devices.find((x) => x.note)) {
      result.push(
        <CommentOutlined
          data-testid="noteIcon"
          key="note-icon"
          sx={{
            width: 16,
            height: 16,
            color: device.note ? Constants.Colors.onSurfaceVariant : 'transparent',
          }}
        />,
      );
    }

    const monitoringIcon = determineMonitoringIcon(device);
    if (monitoringIcon) {
      result.push(monitoringIcon);
    }

    if (readDevicesData.devices.find((x) => x.organisationUnitId)) {
      result.push(
        <LinkOutlined
          data-testid="linkIcon"
          key="link-icon"
          sx={{
            width: 16,
            height: 16,
            color: device.organisationUnitId ? Constants.Colors.onPrimaryContainer : 'transparent',
          }}
        />,
      );
    }

    return result;
  };

  const determineMonitoringIcon = (device: Device): ReactElement | undefined => {
    if (device.status === DeviceStatus.outDated) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.OutDated}`}
          type={MonitoringType.OutDated}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.canBeMonitored && device.status === DeviceStatus.notMonitored) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.NotMonitored}`}
          type={MonitoringType.NotMonitored}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.status === DeviceStatus.offline) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.Offline}`}
          type={MonitoringType.Offline}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.status === DeviceStatus.batteryCritical) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.BatteryCritical}`}
          type={MonitoringType.BatteryCritical}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.status === DeviceStatus.batteryLow) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.BatteryLow}`}
          type={MonitoringType.BatteryLow}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.canBeMonitored && device.status === DeviceStatus.online) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.Online}`}
          type={MonitoringType.Online}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (device.status === DeviceStatus.malfunction) {
      return (
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.DeviceMalfunction}`}
          type={MonitoringType.DeviceMalfunction}
          withTooltip={true}
          visible={true}
          size={16}
        />
      );
    } else if (readDevicesData?.devices.find((x) => x.deviceMonitoring || x.isOutdated)) {
      return (
        <MonitoringIcon key={`monitoring-icon-filler`} type={MonitoringType.BatteryLow} withTooltip={false} visible={false} size={16} />
      );
    }

    return <div key="monitoring-icon-placeholder" className={styles.iconPlaceholder} />;
  };

  const openDetails = (viewingMode: ViewingMode, device: Device | undefined = undefined): void => {
    setCurrentDeviceId(device?.id);
    openDeviceDetailsPopup(false);
    setDetailsViewingMode(viewingMode);
  };

  const openSendTestEvent = (device: Device): void => {
    openSendTestEventPopup(device);
  };

  const getOverflowMenuItems = (): Array<TableOverflowMenuItem<Device>> | undefined => {
    const menuItems: Array<TableOverflowMenuItem<Device>> = [
      {
        label: t('devicesTable.action.view'),
        action: (device) => openDetails('viewing', device),
      },
    ];

    if (hasPermission(Permission.UpdateDevice)) {
      menuItems.push({
        label: t('devicesTable.action.edit'),
        action: (device) => openDetails('editing', device),
      });
    }
    if (hasPermission(Permission.SendTestEvent)) {
      menuItems.push({
        label: t('devicesTable.action.sendTestEvent'),
        action: (device) => openSendTestEvent(device),
        isVisible: (device) =>
          device.organisationUnitId !== null &&
          (device.gateway?.type === GatewayType.IoTEdge || device.gateway?.type === GatewayType.IoTEdgeCluster) &&
          !device?.system &&
          !isG2Device(device?.type),
      });
    }
    if (hasPermission(Permission.DeleteDevice)) {
      menuItems.push(
        {
          label: 'divider',
          isVisible: (device) => !isRoomDevice(device),
        },
        {
          label: t('devicesTable.action.delete'),
          action: (device) => {
            setIsDeletePopupOpen(true);
            setCurrentDeviceId(device.id);
          },
          isVisible: (device) => !isRoomDevice(device),
        },
      );
    }

    return menuItems;
  };

  const handleOnSortChanged = (property: NestedKeyof<Device>, isAscending: boolean): void => {
    setSortFromSessionStorage({
      isAscending: isAscending,
      property: property,
    });

    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: currentRowsPerPage,
    });
  };

  const handleOnOptionsSelected = (filterValueType: FilterValueType, keys: string[]): void => {
    const newFilters = [...deviceFiltersFromSessionStorage.filterValues];
    const indexOfFilterWithSameFilterValueType = newFilters.findIndex((filter) => filter.filterValueType === filterValueType);
    const deviceFilter: DeviceFilter = {
      filterValueType: filterValueType,
      values: keys,
    };

    if (indexOfFilterWithSameFilterValueType >= 0) {
      newFilters[indexOfFilterWithSameFilterValueType] = deviceFilter;
    } else {
      newFilters.push(deviceFilter);
    }

    handleOnFiltersChanged({
      ...deviceFiltersFromSessionStorage,
      filterValues: newFilters,
    });
  };

  const handleOnMonitoringFilterSelected = (selected: boolean, type: MonitoringType): void => {
    const newFilters = [...deviceFiltersFromSessionStorage.monitoringFilterValues];

    if (selected) {
      newFilters.push({ monitoringType: type, count: 0 });
    } else {
      const index = newFilters.findIndex((filter) => filter.monitoringType === type);
      newFilters.splice(index, 1);
    }

    handleOnFiltersChanged({
      ...deviceFiltersFromSessionStorage,
      monitoringFilterValues: newFilters,
    });
  };

  const handleOnFiltersChanged = (deviceFilters: DeviceFilterValues): void => {
    setDeviceFiltersFromSessionStorage(deviceFilters);
    setCurrentPage(0);
    handleOnPageChanged(0);
  };

  const clearFilters = (): void => {
    setDeviceFiltersFromSessionStorage({
      filterValues: [],
      monitoringFilterValues: [],
    });
    setSearchBarFromSessionStorage('');
  };

  const triggerReadDevices = (): void => {
    const readDevicesQuery = {
      pagination: currentPagination,
      sort: sortFromSessionStorage,
      filters: deviceFiltersFromSessionStorage,
      searchQuery: searchBarFromSessionStorage,
    } as ReadDevicesCommand;

    triggerReadDevicesQuery(readDevicesQuery);
    triggerReadFilterValuesQuery(undefined);
  };

  useEffect(() => {
    if (readDevicesError) {
      setErrorMessage(readDevicesError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readDevicesError]);

  useEffect(() => {
    if (readFilterValuesError) {
      setErrorMessage(readFilterValuesError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readFilterValuesError]);

  useEffect(() => {
    if (
      currentPagination.skip !== readDevicesQuery?.pagination.skip ||
      currentPagination.take !== readDevicesQuery?.pagination.take ||
      searchBarFromSessionStorage !== readDevicesQuery?.searchQuery ||
      deviceFiltersFromSessionStorage !== readDevicesQuery.filters
    ) {
      const newReadDevicesQuery = {
        pagination: currentPagination,
        sort: sortFromSessionStorage,
        filters: deviceFiltersFromSessionStorage,
        searchQuery: searchBarFromSessionStorage,
      } as ReadDevicesCommand;

      updateReadDevicesQuery(newReadDevicesQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortFromSessionStorage, currentPagination, deviceFiltersFromSessionStorage, searchBarFromSessionStorage]);

  const isValidFilter = (filter: DeviceFilter): boolean => {
    if (!readFilterValuesData) {
      return false;
    }

    const filterValues = readFilterValuesData.filterValues
      .find((availableFilter) => availableFilter.filterValueType === filter.filterValueType)
      ?.values.map((x) => x.key);

    if (!filterValues) {
      return false;
    }

    return filterValues && filter.values.every((value) => filterValues.includes(value));
  };

  useEffect(() => {
    if (
      deviceFiltersFromSessionStorage?.filterValues &&
      deviceFiltersFromSessionStorage.filterValues.length > 0 &&
      readFilterValuesIsSuccess &&
      readFilterValuesData
    ) {
      const invalidFilters = deviceFiltersFromSessionStorage.filterValues.filter((filter) => !isValidFilter(filter));

      if (invalidFilters.length > 0) {
        setDeviceFiltersFromSessionStorage({
          filterValues: deviceFiltersFromSessionStorage.filterValues.filter((filter) => !invalidFilters.includes(filter)),
          monitoringFilterValues: [],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readFilterValuesData]);

  return (
    <div className={styles.devicesContainer}>
      <PageHeader
        title={t('pageHeaderTitle')}
        addBtnVisible={readDevicesIsSuccess && hasPermission(Permission.CreateDevice)}
        onAdd={() => openDetails('creation')}
        navigationComponent={<IntegrationsNavigation />}
        filterComponent={
          readFilterValuesData ? (
            <DeviceFilters
              onSearchValueChanged={(value: string) => {
                handleOnSearchChanged(value);
                setSearchBarFromSessionStorage(value);
              }}
              activeFilters={deviceFiltersFromSessionStorage?.filterValues ?? []}
              activeMonitoringFilters={deviceFiltersFromSessionStorage.monitoringFilterValues ?? []}
              monitoringValues={readFilterValuesData.monitoringFilterValues}
              filterValues={readFilterValuesData.filterValues}
              onOptionsSelected={handleOnOptionsSelected}
              onMonitoringFilterSelected={handleOnMonitoringFilterSelected}
              clearFilters={clearFilters}
              searchBarValue={searchBarFromSessionStorage}
              refresh={() => {
                triggerReadDevices();
                triggerReadFilterValuesQuery(undefined);
              }}
            />
          ) : undefined
        }
      />
      {(readDevicesIsLoading || readFilterValuesIsLoading) && <LoadingIndicator />}
      {readDevicesIsSuccess && readDevicesData && (
        <Table
          data={readDevicesData.devices.map<RowItem<Device>>((device) => ({
            data: device,
          }))}
          columns={columns}
          overflowMenuOptions={getOverflowMenuItems()}
          onItemClick={(item: Device) => openDetails('viewing', item)}
          pagination={{
            count: readDevicesData.total,
            page: currentPage,
            rowsPerPage: currentRowsPerPage,
            onPageChanged: handleOnPageChanged,
            onRowsPerPageChanged: handleOnRowsPerPageChanged,
          }}
          onSortChanged={handleOnSortChanged}
          rowIdentifier={(item: Device) => item.id ?? ''}
          initialOrderBy={sortFromSessionStorage.property}
          initialOrderDirection={sortFromSessionStorage.isAscending ? 'asc' : 'desc'}
        />
      )}
      {isDeviceDetailsPopupOpen && (
        <DeviceDetailsProvider deviceId={currentDeviceId} initialViewingMode={detailsViewingMode}>
          <DeviceDetailsPopup />
        </DeviceDetailsProvider>
      )}
      {isSendTestEventPopupOpen && <SendTestEventPopup />}
      {isDeletePopupOpen && (
        <DeviceDeletePupup
          device={readDevicesData?.devices.find((device) => device.id === currentDeviceId)}
          handleOnClose={() => setIsDeletePopupOpen(false)}
        />
      )}
    </div>
  );
};

export default Devices;
